import { useEffect, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Dialog } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { SegmentedControl } from '@/components/ui/segmented-control';
import { LanguagePresetCombobox } from '@/components/workspaces/LanguagePresetCombobox';
import { normalizeCode, type PresetLanguage } from '@/lib/languages';

interface Props {
  open: boolean;
  pending: boolean;
  /** Codes already in the workspace; presets for these are disabled. */
  existingCodes?: readonly string[];
  onClose: () => void;
  onSubmit: (input: { code: string; label?: string; pluralForms?: string }) => Promise<void> | void;
}

type Mode = 'preset' | 'custom';

export function AddLanguageDialog({
  open,
  pending,
  existingCodes = [],
  onClose,
  onSubmit,
}: Props) {
  const [mode, setMode] = useState<Mode>('preset');
  const [code, setCode] = useState('');
  const [label, setLabel] = useState('');
  const [pluralForms, setPluralForms] = useState('');

  useEffect(() => {
    if (!open) {
      setMode('preset');
      setCode('');
      setLabel('');
      setPluralForms('');
    }
  }, [open]);

  function applyPreset(preset: PresetLanguage) {
    setCode(preset.code);
    setLabel(preset.label);
    setPluralForms(preset.pluralForms);
  }

  // Accept ISO-639-1, BCP47 (en, en-US, pt-BR, zh-Hans).
  const trimmed = code.trim();
  const codeOk = /^[a-z]{2,3}(-[A-Za-z0-9]{2,8})*$/.test(trimmed);
  const duplicate = existingCodes.some((c) => normalizeCode(c) === normalizeCode(trimmed));
  const canSubmit = codeOk && !duplicate && !pending;

  function submit() {
    if (!canSubmit) return;
    void onSubmit({
      code: trimmed,
      label: label.trim() || undefined,
      pluralForms: pluralForms.trim() || undefined,
    });
  }

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title="Add language"
      description="Adds the language to the workspace pool; projects then opt-in per target."
      footer={
        <>
          <Button variant="outline" onClick={onClose} disabled={pending}>
            Cancel
          </Button>
          <Button onClick={submit} disabled={!canSubmit}>
            {pending ? 'Saving…' : 'Add language'}
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        <SegmentedControl<Mode>
          value={mode}
          onValueChange={setMode}
          ariaLabel="Add language mode"
          options={[
            { value: 'preset', label: 'From list' },
            { value: 'custom', label: 'Custom language' },
          ]}
        />

        {mode === 'preset' && (
          <div className="space-y-1.5">
            <Label>Pick a language</Label>
            <LanguagePresetCombobox existingCodes={existingCodes} onSelect={applyPreset} />
            <p className="text-[11px] text-muted-foreground">
              Selecting a language fills in code, label, and plural forms below — edit any of
              them before saving.
            </p>
          </div>
        )}

        <form
          className="space-y-4"
          onSubmit={(e) => {
            e.preventDefault();
            submit();
          }}
        >
          <div className="space-y-1.5">
            <Label htmlFor="lang-code">Code</Label>
            <Input
              id="lang-code"
              value={code}
              onChange={(e) => setCode(e.target.value.trim())}
              placeholder="pt-BR"
              autoFocus={mode === 'custom'}
              required
            />
            {!codeOk && code.length > 0 && (
              <p className="text-[11px] text-warning">
                Use BCP 47 — e.g. <code>en</code>, <code>pt-BR</code>, <code>zh-Hans</code>.
              </p>
            )}
            {codeOk && duplicate && (
              <p className="text-[11px] text-warning">
                <code>{trimmed}</code> is already in this workspace.
              </p>
            )}
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="lang-label">Display label (optional)</Label>
            <Input
              id="lang-label"
              value={label}
              onChange={(e) => setLabel(e.target.value)}
              placeholder="Portuguese (Brazil)"
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="lang-plural">Plural forms (optional, gettext)</Label>
            <Input
              id="lang-plural"
              value={pluralForms}
              onChange={(e) => setPluralForms(e.target.value)}
              placeholder="nplurals=2; plural=(n != 1);"
            />
          </div>
          <button type="submit" hidden />
        </form>
      </div>
    </Dialog>
  );
}
